import { AI_LABEL_ASSETS, AI_LABEL_COLOUR_HEX, AI_LABEL_TEXT_FONT_FRACTION, AI_LABEL_TEXT_SHADOW, AiLabelState, AiLabelType } from './aiLabel.constants'
import { InterVariableFontFamily } from 'constants/styling/theme'
import { AiLabelRect, getAiLabelRect } from './aiLabelLayout'
import { logger } from 'utils/logging'

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    // Reject with a real Error (not the DOM ErrorEvent) so the logger reports it to Sentry with a stack
    img.onerror = () => reject(new Error(`Failed to load image for AI label: ${src.slice(0, 120)}`))
    img.src = src
  })

/** Whether the current AI label state produces a visible label. */
export const shouldApplyAiLabel = (state: AiLabelState): boolean =>
  state.showAiLabel && (state.labelType !== AiLabelType.TEXT || !!state.labelText.trim())

/**
 * Makes sure the webfont is available to the canvas before measuring or drawing,
 * so the burn-in uses the same face the overlay renders with (both use the theme's
 * InterVariableFontFamily; the literal below must match its first family). Non-fatal
 * on failure - the shared stack falls back identically on both sides.
 */
export async function ensureAiLabelFontLoaded(fontSize: number): Promise<void> {
  try {
    const faces = await document.fonts.load(`${fontSize}px "Inter Variable"`)
    if (!faces.length) {
      // Resolved but matched nothing - the canvas will draw with the fallback face
      logger.warn('AI label webfont "Inter Variable" not matched; using fallback face', undefined, {
        context: { component: 'applyAiLabelToImage', action: 'fontLoad' }
      })
    }
  } catch (error) {
    logger.warn('AI label webfont load failed; using fallback face', error, {
      context: { component: 'applyAiLabelToImage', action: 'fontLoad' }
    })
  }
}

/** Fail loudly on a degenerate rect - canvas draw calls silently no-op on non-finite arguments. */
const assertDrawableRect = (rect: AiLabelRect, state: AiLabelState): void => {
  const values = [rect.left, rect.top, rect.width, rect.height]
  if (!values.every(Number.isFinite) || rect.width <= 0 || rect.height <= 0) {
    throw new Error(`AI label resolved to an undrawable rect (${values.join(', ')}) for ${state.labelType}`)
  }
}

/**
 * Burns the AI label into the image via Canvas, mirroring AiLabelOverlay exactly
 * (same rect from aiLabelLayout, same top-left text anchoring). Used on Save only —
 * the live preview renders the label as a DOM overlay without touching the image data.
 *
 * @param src - Source image URL
 * @param state - Current AI label state
 * @returns Blob URL of the labelled image
 */
export async function applyAiLabelToImage(src: string, state: AiLabelState): Promise<string> {
  const img = await loadImage(src)

  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Could not get canvas context for AI label')
  }

  ctx.drawImage(img, 0, 0)

  const imageSize = { width: canvas.width, height: canvas.height }

  if (state.labelType === AiLabelType.TEXT) {
    const text = state.labelText.trim()
    // Warm the family before measuring (the nominal size only selects a face; any size loads the font)
    await ensureAiLabelFontLoaded(Math.round(imageSize.width * AI_LABEL_TEXT_FONT_FRACTION))
    const rect = getAiLabelRect({ ...state, labelText: text }, imageSize)
    assertDrawableRect(rect, state)
    const fontSize = rect.fontSize ?? 0

    ctx.font = `${fontSize}px ${InterVariableFontFamily}`
    // Top-left anchoring matches the overlay span (lineHeight 1, positioned by rect left/top)
    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    ctx.fillStyle = AI_LABEL_COLOUR_HEX[state.colour]
    // Opacity applies to the label only, never the underlying photo (mirrored in AiLabelOverlay)
    ctx.globalAlpha = state.opacity / 100
    // Soft shadow keeps thin type legible on busy photos (mirrored in AiLabelOverlay)
    ctx.shadowColor = AI_LABEL_TEXT_SHADOW[state.colour]
    ctx.shadowBlur = fontSize * 0.12
    ctx.fillText(text, rect.left, rect.top)
    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0
    ctx.globalAlpha = 1
  } else {
    const badge = await loadImage(AI_LABEL_ASSETS[state.labelType][state.colour])
    if (!badge.naturalWidth || !badge.naturalHeight) {
      // drawImage silently draws nothing for zero-intrinsic-size images - fail loudly instead
      throw new Error(`AI label badge SVG has no intrinsic size: ${state.labelType}/${state.colour}`)
    }
    const rect = getAiLabelRect(state, imageSize)
    assertDrawableRect(rect, state)
    // Opacity applies to the badge only, never the underlying photo (mirrored in AiLabelOverlay)
    ctx.globalAlpha = state.opacity / 100
    ctx.drawImage(badge, rect.left, rect.top, rect.width, rect.height)
    ctx.globalAlpha = 1
  }

  // Export-side parity record: what was burned, where, and at what resolution
  logger.info('AI label burned into export', {
    context: {
      component: 'applyAiLabelToImage',
      action: 'burn',
      metadata: {
        labelType: state.labelType,
        colour: state.colour,
        opacity: state.opacity,
        customPlacement: !!state.position,
        sizeFraction: state.sizeFraction ?? undefined,
        exportSize: `${canvas.width}x${canvas.height}`,
      }
    }
  })

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        const error = new Error('Failed to create blob from canvas')
        logger.error('Failed to create blob from canvas for AI label', error, {
          context: {
            component: 'applyAiLabelToImage',
            action: 'toBlob',
            metadata: {
              labelType: state.labelType,
              canvasWidth: canvas.width,
              canvasHeight: canvas.height
            }
          }
        })
        reject(error)
        return
      }
      resolve(URL.createObjectURL(blob))
    }, 'image/jpeg', 0.9)
  })
}
